import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../../shared/hooks/api";
import { error, success } from "../../../utils/toast";
import { ProductDetailWrapper } from "./Styled";
import { Product, ProductCustomerAddOn } from "../../../data/entities";
import BackButton from "../../../shared/components/BackButton";
import Title from "./Title";
import TabNavigation from "./TabBar";
import PageLoader from "../../../shared/components/LoadingPage";
import apiMethods from "../../../utils/api";
import { ROLE } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import CancelModal from "../../../shared/components/CancelModal";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";

const processProduct = (product: Product) => {
    let materials = [];

    if (product) {
        materials = product.materials.map((item: any) => {
            let providers = [];
            if (item.material) {
                providers = item.material.providers.map((item: any) => {
                    return item;
                });
                return {
                    ...item.material,
                    materialProductId: item.id,
                    quantity: item.quantity,
                    provider_name: item.provider_name,
                    provider_price: item.provider_price,
                    note: item.note,
                    providers: providers,
                    category_note: item.category_note,
                    specification: item.specification,
                    measurement: item.measurement,
                    excess_quantity: item.excess_quantity,
                    excess_price: item.excess_price,
                    total_measurement: item.total_measurement,
                    category_group: item.category_group,
                };
            }
            return item;
        });
    }
    return { ...product, materials };
};

const prepareProduct = (product: Product, role: ROLE) => {
    const processedMaterials = product.materials.map((item: any) => ({
        ...item,
        id: item.materialProductId,
        material_id: item.id,
        quantity: item.quantity,
        provider_name: item.provider_name,
        provider_price: item.provider_price,
        category: item.category,
        note: item.note,
        category_note: item.category_note,
        specification: item.specification,
        measurement: item.measurement,
        excess_quantity: item.excess_quantity,
        excess_price: item.excess_price,
        total_measurement: item.total_measurement,
    }));

    const processedCustomers = product.customers.map((item) => ({
        ...item,
        customer_id: item.customer.id,
        addons: item.addons.map(
            (productCustomerAddon: ProductCustomerAddOn) => ({
                ...productCustomerAddon,
                addon_id: productCustomerAddon.addon.id,
            })
        ),
    }));

    const processedExtraFees = product.extra_fees.map((item: any) => ({
        ...item,
        extra_fee_id: item.extra_fee.id,
    }));

    const processedCategoryGroup = product.category_group.id;

    switch (role) {
        case ROLE.SALE_MANAGER:
        case ROLE.SALE_STAFF:
            return {
                id: product.id,
                product_code: product.product_code,
                name: product.name,
                customers: processedCustomers,
                extra_fees: processedExtraFees,
            };
        case ROLE.MANAGER:
        case ROLE.ACCOUNTANT_MANAGER:
            return {
                ...product,
                materials: processedMaterials,
                customers: processedCustomers,
                extra_fees: processedExtraFees,
                category_group_id: processedCategoryGroup,
            };
        case ROLE.LOGISTIC_STAFF:
            return {
                materials: processedMaterials,
            };
        default:
            return {};
    }
};

const ProductDetail = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");

    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const [
        { data, isLoading, setLocalData, setLocalUrl: setGetUrl },
        fetchProduct,
    ] = api.get(`/products/${productId}/`);

    const productData = useMemo(() => processProduct(data), [data]);

    const [{ newData, isUpdating }, updatingProduct] = api.patch(`/products/${productId}/`);
    const [{ isDeleting }, deleteProduct] = api.delete(`/products/${productId}/`);

    useEffect(() => {
        setGetUrl(`/products/${productId}/`);
    }, [productId, setGetUrl]);

    // Use a direct flag for modifications
    const markAsModified = useCallback(() => {
        setIsModified(true);
    }, []);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const handleUpdate = async () => {
        try {
            const preparedData = prepareProduct(productData, role);
            await updatingProduct(preparedData);
            await fetchProduct();
            setIsModified(false);
            success("THÀNH CÔNG", "Cập nhật sản phẩm thành công");
        } catch (err) {
            error("Lỗi cập nhật đơn hàng", err);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteProduct();
            success("THÀNH CÔNG", "Xóa sản phẩm thành công");
            navigate("/products/");
        } catch (err) {
            error("Lỗi xóa sản phẩm", err);
        }
    };

    const confirmNavigation = useCallback(
        (path: string) => {
            if (isModified) {
                setNavigationPath(path);
                setShowUnsavedDialog(true);
                return false;
            }

            navigate(path);
            return true;
        },
        [isModified, navigate]
    );

    const handleBackClick = () => {
        confirmNavigation("/products/");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        try {
            await handleUpdate();
            navigate(navigationPath);
        } catch (err) {
            error("Lỗi cập nhật sản phẩm", err);
        }
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const handleDataChange = (updateProduct: any) => {
        // Mark as modified on ANY data change
        markAsModified();
        setLocalData({
            ...data,
            ...updateProduct,
        });
    };

    return (
        <ProductDetailWrapper>
            <BackButton label="Trở Về" onClick={handleBackClick} />
            {isLoading && <PageLoader />}

            {!isLoading && (
                <Title
                    title={productData.name}
                    data={productData}
                    setValidated={setValidated}
                    onSubmit={handleUpdate}
                    onDataChange={markAsModified}
                    hasUnsavedChanges={isModified}
                    onCancelClick={() => confirmNavigation("/products/")}
                    isLoading={isUpdating}
                    deleteProduct={handleDelete}
                    isDeleting={isDeleting}
                />
            )}

            {!isLoading && (
                <TabNavigation
                    validated={validated}
                    productData={productData}
                    mergeProduct={handleDataChange}
                />
            )}

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
            />
        </ProductDetailWrapper>
    );
};

export default ProductDetail;
